import React, { useState, useMemo, useEffect } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * TherapeuticAlternativesTable - Displays therapeutic alternatives by drug class
 */
const TherapeuticAlternativesTable = ({ 
  alternatives, 
  originalTherapeuticClassId,
  searchQuery = ""
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  
  const itemsPerPage = 15;

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Group alternatives by drug name to collect all strengths
  const groupedAlternatives = useMemo(() => {
    if (!alternatives || alternatives.length === 0) {
      return [];
    }
    
    const groups = new Map();
    
    alternatives.forEach(alt => {
      const key = alt.drugName;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(alt);
    });
    
    // Convert to array with combined strengths
    return Array.from(groups.values()).map(group => {
      const first = group[0];
      const strengths = [...new Set(group.map(g => `${g.strength}${g.unitOfMeasure}`))].join(', ');
      
      return {
        ...first,
        typicalStrengths: strengths,
        productCount: group.length,
      };
    });
  }, [alternatives]);

  const sortedAlternatives = useMemo(() => {
    if (!sortColumn || !sortDirection) return groupedAlternatives;
    
    return [...groupedAlternatives].sort((a, b) => {
      let compareA;
      let compareB;
      
      switch (sortColumn) {
        case 'drugName':
          compareA = a.drugName.toLowerCase();
          compareB = b.drugName.toLowerCase();
          break;
        case 'classSimilarity':
          compareA = a.therapeuticClassId === originalTherapeuticClassId ? 0 : 1;
          compareB = b.therapeuticClassId === originalTherapeuticClassId ? 0 : 1;
          break;
        case 'form':
          compareA = (a.doseForm || '').toLowerCase();
          compareB = (b.doseForm || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groupedAlternatives, sortColumn, sortDirection, originalTherapeuticClassId]);

  // Apply search filter
  const searchFilteredAlternatives = useMemo(() => {
    if (!searchQuery.trim()) return sortedAlternatives;
    
    const query = searchQuery.toLowerCase();
    return sortedAlternatives.filter(alt => 
      alt.drugName.toLowerCase().includes(query) ||
      (alt.drugDescription && alt.drugDescription.toLowerCase().includes(query)) ||
      alt.therapeuticClass.toLowerCase().includes(query) ||
      (alt.doseForm && alt.doseForm.toLowerCase().includes(query))
    );
  }, [sortedAlternatives, searchQuery]);

  const totalPages = Math.ceil(searchFilteredAlternatives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAlternatives = searchFilteredAlternatives.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortColumn, sortDirection, searchQuery]);

  const SortableTableHead = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th className="cursor-pointer hover:bg-muted/50 transition-colors p-3" onClick={() => handleSort(column)}>
        <div className="flex items-center gap-2">
          <span className="font-medium">{children}</span>
          {isActive ? (
            sortDirection === 'asc' ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            <div className="h-4 w-4 opacity-30">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
              </svg>
            </div>
          )}
        </div>
      </th>
    );
  };

  const getClassSimilarityBadge = (alt) => {
    if (originalTherapeuticClassId && alt.therapeuticClassId === originalTherapeuticClassId) {
      return (
        <div>
          <Badge variant="static" className="bg-status-info/10 text-status-info border-status-info/30">
            Same Class
          </Badge>
          <div className="text-sm text-muted-foreground mt-1">{alt.therapeuticClass}</div>
        </div>
      );
    }
    return (
      <div>
        <Badge variant="static" className="border-status-restricted/30 bg-status-restricted/5 text-status-restricted">
          Different Class
        </Badge>
        <div className="text-sm text-muted-foreground mt-1">{alt.therapeuticClass}</div>
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pages.push(
          <button
            key={i}
            onClick={() => setCurrentPage(i)}
            className={`px-3 py-1 rounded ${
              currentPage === i
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            {i}
          </button>
        );
      } else if (i === currentPage - 2 || i === currentPage + 2) {
        pages.push(<span key={i} className="px-2">...</span>);
      }
    }

    return (
      <div className="flex justify-between items-center mt-6 px-4 py-4">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-{Math.min(endIndex, searchFilteredAlternatives.length)} of {searchFilteredAlternatives.length} alternatives
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {pages}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Early return check after all hooks
  if (!alternatives || alternatives.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Warning Alert */}
      <Alert className="border-status-restricted/30 bg-status-restricted/5 text-status-restricted [&>svg]:relative [&>svg]:left-0 [&>svg]:top-0 [&>svg~*]:pl-0 flex items-center gap-2">
        <svg className="h-5 w-5 shrink-0 -mt-[1px]" style={{ position: 'relative', color: 'inherit' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <AlertDescription className="flex-1">
          These are therapeutic alternatives based on drug class similarity. They may not be covered under the selected plan.
        </AlertDescription>
      </Alert>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto max-h-[600px] overflow-y-auto border rounded">
        <table className="w-full">
          <thead className="sticky top-0 bg-background border-b">
            <tr>
              <SortableTableHead column="drugName">Drug Name</SortableTableHead>
              <SortableTableHead column="classSimilarity">Class Similarity</SortableTableHead>
              <SortableTableHead column="form">Form</SortableTableHead>
              <th className="p-3 text-left font-medium">Typical Strength</th>
            </tr>
          </thead>
          <tbody>
            {paginatedAlternatives.map((alt) => (
              <tr key={alt.productId} className="border-b">
                <td className="p-3">
                  <div className="font-medium">{alt.drugName}</div>
                  {alt.drugDescription && alt.drugDescription.toLowerCase() !== alt.drugName.toLowerCase() && (
                    <div className="text-sm text-muted-foreground mt-0.5">{alt.drugDescription}</div>
                  )}
                  <Badge variant="static" className="text-xs mt-1 bg-white text-black border-gray-300">
                    {alt.isGeneric ? "Generic" : "Brand"}
                  </Badge>
                </td>
                <td className="p-3">
                  {getClassSimilarityBadge(alt)}
                </td>
                <td className="p-3">
                  <span className="text-sm">{alt.doseForm || "—"}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm">{alt.typicalStrengths || "—"}</span>
                  {alt.productCount > 1 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {alt.productCount} variations
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {renderPagination()}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {paginatedAlternatives.map((alt) => (
          <Card key={alt.productId} className="p-4">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-medium">{alt.drugName}</div>
                  {alt.drugDescription && alt.drugDescription.toLowerCase() !== alt.drugName.toLowerCase() && (
                    <div className="text-sm text-muted-foreground mt-0.5">{alt.drugDescription}</div>
                  )}
                </div>
              </div>

              <Badge variant="static" className="text-xs bg-white text-black border-gray-300">
                {alt.isGeneric ? "Generic" : "Brand"}
              </Badge>

              <div>
                <div className="text-sm font-medium mb-1">Class Similarity</div>
                {getClassSimilarityBadge(alt)}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="font-medium">Form</div>
                  <div className="text-muted-foreground">{alt.doseForm || "—"}</div>
                </div>
                <div>
                  <div className="font-medium">Typical Strength</div>
                  <div className="text-muted-foreground">{alt.typicalStrengths || "—"}</div>
                  {alt.productCount > 1 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {alt.productCount} variations
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
        
        {/* Pagination - Mobile */}
        {totalPages > 1 && (
          <div className="flex flex-col gap-3 mt-4">
            <div className="text-sm text-muted-foreground text-center">
              Page {currentPage} of {totalPages} ({searchFilteredAlternatives.length} alternatives)
            </div>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapeuticAlternativesTable;
